// Каталог показателей в левой панели: разделы, поиск, выбор источника.

import type { Indicator } from '../types';
import { api } from '../api';
import { formatNumber as formatLocalizedNumber, t } from '../i18n';

import { controls } from './controls';
import { currentLocale } from './locale';
import { emissCatalogUserSummary } from './emiss';
import { escapeHtml } from './dom';
import { state } from './state';
import { syncChoiceMirrors } from './choice-lists';
import { updateSubmitState } from './widget-form';
import { updateTerritoryCount } from './territories';
import { worldBankCatalogUserSummary } from './worldbank';

export function compactIndicatorOptionLabel(indicator: Indicator): string {
  const source = indicator.short_name || indicator.name;
  const replacements: Array<[RegExp, string]> = [
    [/^оценка\s+численности\s+населения\s+на\s+1\s+января.*$/i, 'Численность населения'],
    [/^число\s+родившихся.*$/i, 'Родившиеся'],
    [/^число\s+умерших.*$/i, 'Умершие'],
    [/^естественный\s+прирост.*$/i, 'Естественный прирост'],
    [/^миграционный\s+прирост.*$/i, 'Миграция'],
  ];
  const compact = replacements.reduce((value, [pattern, replacement]) => (
    pattern.test(value) ? value.replace(pattern, replacement) : value
  ), source.trim());
  if (compact.length <= 42) return compact;
  const clean = compact.split(/[;,(]/)[0]?.trim() || compact;
  return clean.length <= 42 ? clean : `${clean.slice(0, 39).trim()}…`;
}

export function selectedIndicatorLabel(indicator: Indicator): string {
  const compact = compactIndicatorOptionLabel(indicator)
    .replace(/\s*\((?:ЕМИСС|EMISS)[^)]+\)\s*$/i, '')
    .replace(/\s*·\s*(?:ЕМИСС|БДМО|BDMO|населённые пункты|демонстрация)\s*$/i, '')
    .trim();
  return compact || (indicator.short_name ?? indicator.name);
}

export function normalizeClientSearch(value: unknown): string {
  return String(value ?? '')
    .toLocaleLowerCase(currentLocale() === 'en' ? 'en-US' : 'ru-RU')
    .replaceAll('ё', 'е')
    .trim();
}

export function indicatorSectionId(indicator: Indicator): string {
  const metadata = indicator.source_metadata ?? {};
  if (indicator.source_type === 'bdmo') {
    const code = metadata.section_code ?? metadata.section;
    return code == null ? '' : String(code);
  }
  if (indicator.source_type === 'settlements') return 'settlements';
  if (indicator.source_type === 'emiss') {
    const text = normalizeClientSearch(`${indicator.name} ${metadata.title_en ?? ''}`);
    if (/(рождаем|родив|семь|брак|развод)/.test(text)) return 'fertility_family';
    if (/(смерт|умер|здоров|заболев|болезн|ожидаем)/.test(text)) return 'mortality_health';
    if (/(миграц|прибыв|выбыв)/.test(text)) return 'migration';
    if (/(доход|зарплат|валов|продукт|инфляц|цен|инвест|оборот)/.test(text)) return 'economy_income';
    if (/(занято|безработ|труд|работник)/.test(text)) return 'labor_employment';
    if (/(жиль|жил|коммун|инфраструкт|строитель|дорог|водоснаб|газ)/.test(text)) return 'housing_infrastructure';
    if (/(образован|школ|детск|культур|социаль|пенси|пособ)/.test(text)) return 'education_social';
    if (/(насел|числен|демограф)/.test(text)) return 'population';
    return 'other';
  }
  return '';
}

export function indicatorMatchesSearch(indicator: Indicator, query: string): boolean {
  const needle = normalizeClientSearch(query);
  if (!needle) return true;
  const metadata = indicator.source_metadata ?? {};
  const haystack = normalizeClientSearch([
    indicator.id,
    indicator.name,
    indicator.short_name,
    indicator.unit,
    indicator.source_name,
    indicator.description,
    metadata.section,
    metadata.section_code,
    metadata.name_en,
    metadata.name_ru,
    metadata.worldbank_indicator_id,
  ].join(' '));
  if (haystack.includes(needle)) return true;
  return needle.split(/\s+/).filter(Boolean).every((token) => haystack.includes(token));
}

export function indicatorMatchesSection(indicator: Indicator, sectionId: string): boolean {
  if (!sectionId) return true;
  return indicatorSectionId(indicator) === sectionId;
}

export function renderIndicatorSections(): void {
  const source = controls.source.value;
  const selected = controls.indicatorSection.value;
  const options = state.indicatorSections.map((section) => (
    `<option value="${escapeHtml(section.id)}">${escapeHtml(section.title)} (${formatLocalizedNumber(section.count, currentLocale(), 0)})</option>`
  ));
  controls.indicatorSection.innerHTML = [`<option value="">${escapeHtml(t(currentLocale(), 'allSections'))}</option>`, ...options].join('');
  controls.indicatorSection.value = state.indicatorSections.some((section) => section.id === selected) ? selected : '';
  controls.indicatorSection.disabled = !source;
}

export async function loadIndicatorSections(source = controls.source.value): Promise<void> {
  const requestToken = ++state.sectionRequestToken;
  if (!source) {
    state.indicatorSections = [];
    renderIndicatorSections();
    return;
  }
  state.indicatorSections = [];
  renderIndicatorSections();
  try {
    const sections = await api.indicatorSections(source, currentLocale());
    if (requestToken !== state.sectionRequestToken || controls.source.value !== source) return;
    state.indicatorSections = sections;
  } catch {
    if (requestToken !== state.sectionRequestToken || controls.source.value !== source) return;
    state.indicatorSections = [];
  }
  renderIndicatorSections();
}

export function populateIndicators(): void {
  const source = controls.source?.value ?? '';
  const sectionId = controls.indicatorSection.value;
  const query = controls.indicatorSearch.value.trim();
  const visibleIndicators = state.indicators.filter((indicator) => {
    if (indicator.demo || indicator.source_type === 'demo') return false;
    if (!source) return indicatorMatchesSearch(indicator, query);
    const sourceMatches = indicator.source_type === source || indicator.id.startsWith(`${source}:`);
    return sourceMatches && indicatorMatchesSection(indicator, sectionId) && indicatorMatchesSearch(indicator, query);
  });
  const selected = controls.indicator.value;
  const options = visibleIndicators.map((indicator) => {
      const suffix = source
        ? ''
        : indicator.source_type === 'bdmo'
          ? ' · муниципальная статистика'
        : indicator.source_type === 'emiss'
            ? ' · региональная статистика'
            : indicator.source_type === 'worldbank'
              ? ' · международная статистика'
            : indicator.source_type === 'settlements'
              ? ' · населённые пункты'
                : '';
      const label = compactIndicatorOptionLabel(indicator);
      return `<option value="${escapeHtml(indicator.id)}" title="${escapeHtml(indicator.name + suffix)}">${escapeHtml(label)}${escapeHtml(suffix)}</option>`;
    });
  const placeholder = source === 'emiss'
    ? '<option value="">Найдите показатель в каталоге ЕМИСС</option>'
    : source === 'worldbank'
      ? '<option value="">Найдите показатель в каталоге международной статистики</option>'
    : visibleIndicators.length
      ? ''
      : '<option value="">Нет доступных показателей</option>';
  controls.indicator.innerHTML = [placeholder, ...options].join('');
  if (visibleIndicators.some((indicator) => indicator.id === selected)) {
    controls.indicator.value = selected;
  } else if (source && source !== 'emiss' && source !== 'worldbank' && visibleIndicators.length) {
    const preferred = source === 'bdmo'
      ? visibleIndicators.find((indicator) => indicator.id === 'bdmo:Y48112027')
      : undefined;
    controls.indicator.value = preferred?.id ?? visibleIndicators[0]?.id ?? '';
  } else {
    controls.indicator.value = '';
  }
  syncSourceCatalogPickerVisibility();
}

export function clearIndicatorDependentControls(message = ''): void {
  controls.indicatorDescription.textContent = message;
  controls.years.innerHTML = '';
  controls.months.innerHTML = '';
  controls.monthField.hidden = true;
  controls.periodSemantics.hidden = true;
  controls.periodSemantics.innerHTML = '';
  state.dimensions = [];
  controls.dimensionFields.innerHTML = '';
  controls.dimensionFields.hidden = true;
  state.catalogTerritories = [];
  controls.districtFilter.innerHTML = '<option value="">Все округа</option>';
  controls.regionFilter.innerHTML = '<option value="">Все субъекты</option>';
  controls.territories.innerHTML = '';
  syncChoiceMirrors();
  controls.territorySearchField.hidden = true;
  controls.territorySearch.value = '';
  updateTerritoryCount();
  updateAdvisor();
}

export function renderLeftEmissSelected(): void {
  if (controls.source.value !== 'emiss') {
    controls.leftEmissSelected.hidden = true;
    return;
  }
  const indicator = currentIndicator();
  controls.leftEmissSelected.hidden = false;
  controls.leftEmissSelected.innerHTML = indicator && isEmissIndicator(indicator)
    ? `<strong>Показатель выбран</strong><span title="${escapeHtml(indicator.name)}">${escapeHtml(selectedIndicatorLabel(indicator))}</span>`
    : '<strong>Показатель не выбран</strong><span>Найдите показатель fedstat.ru и подключите его через исследование.</span>';
}

export function renderLeftWorldBankSelected(): void {
  if (controls.source.value !== 'worldbank') {
    controls.leftWorldBankSelected.hidden = true;
    return;
  }
  const indicator = currentIndicator();
  controls.leftWorldBankSelected.hidden = false;
  controls.leftWorldBankSelected.innerHTML = indicator && isWorldBankIndicator(indicator)
    ? `<strong>Показатель выбран</strong><span title="${escapeHtml(indicator.name)}">${escapeHtml(selectedIndicatorLabel(indicator))}</span>`
    : '<strong>Показатель не выбран</strong><span>Найдите международный показатель и подключите его одним нажатием.</span>';
}

export function syncSourceCatalogPickerVisibility(): void {
  const emissSource = controls.source.value === 'emiss';
  const worldBankSource = controls.source.value === 'worldbank';
  controls.leftEmissCatalogPicker.hidden = !emissSource;
  controls.leftWorldBankCatalogPicker.hidden = !worldBankSource;
  controls.indicatorField.hidden = emissSource || worldBankSource;
  controls.indicator.required = !emissSource && !worldBankSource;
  if (emissSource) {
    controls.leftEmissCatalogStatus.textContent = emissCatalogUserSummary(state.emissCatalogStatus);
    renderLeftEmissSelected();
  } else {
    controls.leftEmissSelected.hidden = true;
  }
  if (worldBankSource) {
    controls.leftWorldBankCatalogStatus.textContent = worldBankCatalogUserSummary(state.worldBankCatalogStatus);
    renderLeftWorldBankSelected();
  } else {
    controls.leftWorldBankSelected.hidden = true;
  }
}

export function syncEmissCatalogPickerVisibility(): void {
  syncSourceCatalogPickerVisibility();
}

export function sourceFilterForIndicator(indicator: Indicator | undefined): string {
  if (!indicator) return '';
  if (indicator.source_type === 'bdmo' || indicator.id.startsWith('bdmo:')) return 'bdmo';
  if (indicator.source_type === 'emiss') return 'emiss';
  if (indicator.source_type === 'worldbank' || indicator.id.startsWith('worldbank:')) return 'worldbank';
  if (indicator.source_type === 'settlements' || indicator.id.startsWith('settlements:')) return 'settlements';
  return '';
}

export function isBdmoIndicator(indicator: Indicator | undefined = currentIndicator()): boolean {
  return indicator?.source_type === 'bdmo' || indicator?.id.startsWith('bdmo:') === true;
}

export function isSettlementIndicator(indicator: Indicator | undefined = currentIndicator()): boolean {
  return indicator?.source_type === 'settlements' || indicator?.id.startsWith('settlements:') === true;
}

export function isEmissIndicator(indicator: Indicator | undefined = currentIndicator()): boolean {
  return indicator?.source_type === 'emiss';
}

export function isWorldBankIndicator(indicator: Indicator | undefined = currentIndicator()): boolean {
  return indicator?.source_type === 'worldbank' || indicator?.id.startsWith('worldbank:') === true;
}

export function currentIndicator(): Indicator | undefined {
  return state.indicators.find((indicator) => indicator.id === controls.indicator.value);
}

export function updateAdvisor(): void {
  updateSubmitState();
}
